import React, { useEffect, useMemo, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SimpleSelect } from "@/components/ui/simple-select";
import { Clock, Loader2, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const emptyForm = {
  date: today(),
  hours: "",
  description: "",
  project_id: "",
  billable: true,
  hourly_rate: "",
};

export function TimeEntries() {
  const { company } = useCompany();
  const companyId = company?._id;

  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // Filter
  const [filter, setFilter] = useState({
    project_id: "",
    from_date: firstOfMonth(),
    to_date: today(),
  });

  // Pagination
  const PAGE_SIZE = 50;
  const [skip, setSkip] = useState(0);

  // Summary
  const [summary, setSummary] = useState(null);

  const loadProjects = async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/projects`);
      setProjects(r.data || []);
    } catch (e) {
      // still allow time-tracking without projects
    }
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (filter.project_id) params.project_id = filter.project_id;
      if (filter.from_date) params.from_date = filter.from_date;
      if (filter.to_date) params.to_date = filter.to_date;
      const r = await apiClient.get(`/companies/${companyId}/time-entries`, {
        params,
      });
      setEntries(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    if (!companyId) return;
    try {
      const d = new Date(filter.from_date || today());
      const r = await apiClient.get(
        `/companies/${companyId}/time-entries/summary`,
        {
          params: { year: d.getFullYear(), month: d.getMonth() + 1 },
        }
      );
      setSummary(r.data);
    } catch (e) {
      setSummary(null);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [companyId]);

  useEffect(() => {
    load();
    loadSummary();
  }, [companyId, skip, filter.project_id, filter.from_date, filter.to_date]);

  const projectOptions = useMemo(() => {
    return [
      { value: "", label: "Alle Projekte" },
      ...projects.map((p) => ({ value: p.id, label: `${p.code} – ${p.name}` })),
    ];
  }, [projects]);

  const projectOptionsForForm = useMemo(() => {
    return [
      { value: "", label: "Kein Projekt" },
      ...projects.map((p) => ({ value: p.id, label: `${p.code} – ${p.name}` })),
    ];
  }, [projects]);

  const resetForm = () => {
    setForm(emptyForm);
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.hours || !form.description) {
      toast.error("Datum, Stunden und Beschreibung sind pflichtig.");
      return;
    }
    const hrs = parseFloat(form.hours);
    if (!hrs || hrs <= 0 || hrs > 24) {
      toast.error("Stunden muss zwischen 0 und 24 liegen.");
      return;
    }
    const payload = {
      date: form.date,
      hours: hrs,
      description: form.description,
      project_id: form.project_id || null,
      billable: !!form.billable,
      hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
    };
    try {
      await apiClient.post(`/companies/${companyId}/time-entries`, payload);
      toast.success("Zeit erfasst.");
      resetForm();
      setSkip(0);
      await load();
      await loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anlegen fehlgeschlagen.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    try {
      await apiClient.delete(`/companies/${companyId}/time-entries/${id}`);
      toast.success("Gelöscht.");
      await load();
      await loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const totalHours = useMemo(
    () => entries.reduce((s, e) => s + (e.hours || 0), 0),
    [entries]
  );
  const billableHours = useMemo(
    () => entries.filter((e) => e.billable).reduce((s, e) => s + (e.hours || 0), 0),
    [entries]
  );

  const projLookup = useMemo(() => {
    const m = {};
    for (const p of projects) m[p.id] = p;
    return m;
  }, [projects]);

  return (
    <div className="p-6 space-y-5" data-testid="time-entries-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center">
            <Clock className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Zeiterfassung</h1>
            <p className="text-sm text-slate-500">
              Erfasse Arbeitszeit pro Projekt — verrechenbare Stunden fließen in Umsatz-Aggregate.
            </p>
          </div>
        </div>
        <Button
          onClick={() => {
            if (showForm) resetForm();
            else setShowForm(true);
          }}
          data-testid="new-time-entry-toggle-button"
        >
          {showForm ? <X className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          {showForm ? "Abbrechen" : "Neuer Eintrag"}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4 border border-slate-200" data-testid="time-kpi-total">
          <div className="text-xs text-slate-500 uppercase">Stunden (Seite)</div>
          <div className="text-2xl font-semibold text-slate-900">{totalHours.toFixed(2)}</div>
        </Card>
        <Card className="p-4 border border-slate-200" data-testid="time-kpi-billable">
          <div className="text-xs text-slate-500 uppercase">Verrechenbar (Seite)</div>
          <div className="text-2xl font-semibold text-emerald-600">{billableHours.toFixed(2)}</div>
        </Card>
        <Card className="p-4 border border-slate-200" data-testid="time-kpi-month">
          <div className="text-xs text-slate-500 uppercase">Monats-Umsatz (verrechenbar)</div>
          <div className="text-2xl font-semibold text-indigo-600">
            CHF {(summary?.rows || []).reduce((s, r) => s + (r.revenue || 0), 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </Card>
      </div>

      {showForm && (
        <Card className="p-4 border border-sky-200 bg-sky-50/40">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="time-entry-form">
            <div>
              <Label className="text-xs">Datum</Label>
              <SwissDateField  value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="time-entry-date-input" />
            </div>
            <div>
              <Label className="text-xs">Stunden</Label>
              <Input type="number" step="0.25" min="0.1" max="24" value={form.hours} onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))} data-testid="time-entry-hours-input" />
            </div>
            <div>
              <Label className="text-xs">Projekt</Label>
              <SimpleSelect value={form.project_id} onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))} options={projectOptionsForForm} data-testid="time-entry-project-select" />
            </div>
            <div>
              <Label className="text-xs">Stundensatz (optional)</Label>
              <Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setForm((f) => ({ ...f, hourly_rate: e.target.value }))} data-testid="time-entry-rate-input" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Beschreibung</Label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="time-entry-description-input" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.billable}
                  onChange={(e) => setForm((f) => ({ ...f, billable: e.target.checked }))}
                  data-testid="time-entry-billable-checkbox"
                />
                verrechenbar
              </label>
            </div>
            <div className="md:col-span-4 flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={resetForm}>Abbrechen</Button>
              <Button type="submit" data-testid="time-entry-submit-button">Erfassen</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="p-3 border border-slate-200" data-testid="time-entries-filter">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Projekt</Label>
            <SimpleSelect value={filter.project_id} onValueChange={(v) => { setSkip(0); setFilter((f) => ({ ...f, project_id: v })); }} options={projectOptions} data-testid="filter-project-select" />
          </div>
          <div>
            <Label className="text-xs">Von</Label>
            <SwissDateField  value={filter.from_date} onChange={(e) => { setSkip(0); setFilter((f) => ({ ...f, from_date: e.target.value })); }} data-testid="filter-from-date" />
          </div>
          <div>
            <Label className="text-xs">Bis</Label>
            <SwissDateField  value={filter.to_date} onChange={(e) => { setSkip(0); setFilter((f) => ({ ...f, to_date: e.target.value })); }} data-testid="filter-to-date" />
          </div>
          <div className="text-xs text-slate-500">Seite {Math.floor(skip / PAGE_SIZE) + 1}</div>
        </div>
      </Card>

      <Card className="border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400" data-testid="time-entries-loading">
            <Loader2 className="h-5 w-5 inline animate-spin mr-2" /> Lade …
          </div>
        ) : entries.length === 0 ? (
          <div className="p-10 text-center text-slate-400" data-testid="time-entries-empty">
            Keine Einträge im gewählten Zeitraum.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="time-entries-table">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="text-left p-3">Datum</th>
                <th className="text-left p-3">Projekt</th>
                <th className="text-left p-3">Beschreibung</th>
                <th className="text-right p-3">Stunden</th>
                <th className="text-right p-3">Satz</th>
                <th className="text-center p-3">Typ</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((en) => {
                const p = projLookup[en.project_id];
                return (
                  <tr key={en.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`time-entry-row-${en.id}`}>
                    <td className="p-3 whitespace-nowrap">{formatDate(en.date)}</td>
                    <td className="p-3 text-xs">{p ? `${p.code} – ${p.name}` : <span className="text-slate-400">–</span>}</td>
                    <td className="p-3">{en.description}</td>
                    <td className="p-3 text-right font-medium">{en.hours.toFixed(2)}</td>
                    <td className="p-3 text-right">{en.hourly_rate ? `CHF ${Number(en.hourly_rate).toFixed(2)}` : "–"}</td>
                    <td className="p-3 text-center">
                      {en.billable ? (
                        <Badge className="bg-emerald-100 text-emerald-700">verrechenbar</Badge>
                      ) : (
                        <Badge variant="outline">intern</Badge>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <Button size="icon" variant="ghost" onClick={() => remove(en.id)} data-testid={`time-entry-delete-${en.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {entries.length > 0 ? (
        <div className="flex justify-between items-center" data-testid="pagination-bar">
          <Button
            variant="outline"
            size="sm"
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            data-testid="time-entries-prev-button"
          >
            ← Zurück
          </Button>
          <span className="text-xs text-slate-500">
            {skip + 1}–{skip + entries.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={entries.length < PAGE_SIZE}
            onClick={() => setSkip(skip + PAGE_SIZE)}
            data-testid="time-entries-next-button"
          >
            Weiter →
          </Button>
        </div>
      ) : (
        <div className="flex justify-between items-center opacity-40" data-testid="pagination-bar-empty">
          <Button variant="outline" size="sm" disabled data-testid="time-entries-prev-button">← Zurück</Button>
          <span className="text-xs text-slate-500">Keine Einträge</span>
          <Button variant="outline" size="sm" disabled data-testid="time-entries-next-button">Weiter →</Button>
        </div>
      )}
    </div>
  );
}

export default TimeEntries;
